import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Stethoscope, Calendar, DollarSign, FileText, AlertTriangle } from 'lucide-react';
import { petsService, PetHealthRecord, Pet } from '../../lib/petsService';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Modal } from '../ui/modal';

interface MedicalHistoryProps {
  selectedPetId?: string;
}

const healthTypeOptions = [
  { value: 'checkup', label: 'Checkup' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'dental', label: 'Dental' },
  { value: 'grooming', label: 'Grooming' },
  { value: 'other', label: 'Other' }
];

export function MedicalHistory({ selectedPetId }: MedicalHistoryProps) {
  const [pets, setPets] = useState<Pet[]>([]);
  const [healthRecords, setHealthRecords] = useState<PetHealthRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<PetHealthRecord | null>(null);
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [petsData, healthData] = await Promise.all([
        petsService.getPets(),
        selectedPetId ? petsService.getPetHealthRecords(selectedPetId) : petsService.getPetHealthRecords()
      ]);
      
      setPets(petsData);
      setHealthRecords(healthData);
      
      if (selectedPetId) {
        const pet = petsData.find(p => p.id === selectedPetId);
        setSelectedPet(pet || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this health record?')) {
      return;
    }

    try {
      await petsService.deletePetHealthRecord(id);
      setHealthRecords(healthRecords.filter(record => record.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete health record');
    }
  };

  const handleEditRecord = (record: PetHealthRecord) => {
    setSelectedRecord(record);
    setShowEditModal(true);
  };

  const handleAddSuccess = () => {
    fetchData();
    setShowAddModal(false);
  };

  const handleEditSuccess = () => {
    fetchData();
    setShowEditModal(false);
    setSelectedRecord(null);
  };

  const getPetName = (petId: string) => {
    const pet = pets.find(p => p.id === petId);
    return pet?.name || 'Unknown Pet';
  };

  const getHealthTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      vaccination: 'bg-blue-50 text-blue-600 border-blue-200',
      checkup: 'bg-green-50 text-green-600 border-green-200',
      surgery: 'bg-red-50 text-red-600 border-red-200',
      injury: 'bg-orange-50 text-orange-600 border-orange-200',
      illness: 'bg-purple-50 text-purple-600 border-purple-200',
      dental: 'bg-cyan-50 text-cyan-600 border-cyan-200',
      parasite_treatment: 'bg-yellow-50 text-yellow-600 border-yellow-200',
      other: 'bg-gray-50 text-gray-600 border-gray-200'
    };
    return colors[type] || colors.other;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchData();
  }, [selectedPetId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Medical History</h2>
          <p className="text-gray-600 mt-1">
            {selectedPet 
              ? `Track ${selectedPet.name}'s medical records and treatments` 
              : 'Track your pets\' medical records and treatments'
            }
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          <Plus size={20} className="mr-2" />
          Add Health Record
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Health Records Grid */}
      {healthRecords.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {healthRecords.map((record) => (
            <motion.div
              key={record.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                {/* Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Stethoscope size={20} className="text-red-600" />
                      <h3 className="text-lg font-semibold text-gray-900">{record.type.charAt(0).toUpperCase() + record.type.slice(1)}</h3>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRecord(record)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRecord(record.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>

                  {/* Pet Name */}
                  <p className="text-sm text-gray-600 mb-2">
                    For: <span className="font-medium">{getPetName(record.pet_id)}</span>
                  </p>

                  {/* Health Type Badge */}
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getHealthTypeColor(record.type)}`}>
                    {record.type.replace('_', ' ').charAt(0).toUpperCase() + record.type.replace('_', ' ').slice(1)}
                  </div>
                </div>

                {/* Details */}
                <div className="p-6 space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar size={16} className="mr-2 text-gray-400" />
                    <span className="font-medium mr-2">Date:</span>
                    {formatDate(record.date)}
                  </div>

                  {record.vet_name && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Vet:</span>
                      {record.vet_name}
                      {record.vet_clinic && ` - ${record.vet_clinic}`}
                    </div>
                  )}

                  {record.diagnosis && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-700">Diagnosis:</span>
                      <p className="text-sm text-gray-600">{record.diagnosis}</p>
                    </div>
                  )}

                  {record.treatment && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-700">Treatment:</span>
                      <p className="text-sm text-gray-600">{record.treatment}</p>
                    </div>
                  )}

                  {record.medications && record.medications.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-700">Medications:</span>
                      <div className="flex flex-wrap gap-1">
                        {record.medications.map((medication, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                          >
                            {medication}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {record.cost && (
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign size={16} className="mr-2 text-gray-400" />
                      <span className="font-medium mr-2">Cost:</span>
                      ${record.cost.toFixed(2)}
                    </div>
                  )}

                  {record.next_due_date && (
                    <div className="flex items-center text-sm text-amber-600">
                      <AlertTriangle size={16} className="mr-2" />
                      <span className="font-medium mr-2">Next Due:</span>
                      {formatDate(record.next_due_date)}
                    </div>
                  )}

                  {record.attachments && record.attachments.length > 0 && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FileText size={16} className="mr-2 text-gray-400" />
                      <span className="font-medium mr-2">Attachments:</span>
                      {record.attachments.length} file(s)
                    </div>
                  )}

                  {record.notes && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-sm text-gray-600 italic">"{record.notes}"</p>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Stethoscope size={48} className="text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Health Records Yet</h3>
          <p className="text-gray-600 mb-6">
            {selectedPet 
              ? `Start tracking ${selectedPet.name}'s medical history` 
              : 'Start tracking your pets\' medical history and treatments'
            }
          </p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Plus size={20} className="mr-2" />
            Add First Health Record
          </Button>
        </div>
      )}

      {/* Add Health Record Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Health Record"
      >
        <AddHealthRecordForm
          pets={pets}
          selectedPetId={selectedPetId}
          onSuccess={handleAddSuccess}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      {/* Edit Health Record Modal */}
      {showEditModal && selectedRecord && (
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedRecord(null);
          }}
          title="Edit Health Record"
        >
          <EditHealthRecordForm
            record={selectedRecord}
            pets={pets}
            onSuccess={handleEditSuccess}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedRecord(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}

// Add Health Record Form Component
interface AddHealthRecordFormProps {
  pets: Pet[];
  selectedPetId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function AddHealthRecordForm({ pets, selectedPetId, onSuccess, onCancel }: AddHealthRecordFormProps) {
  const [formData, setFormData] = useState({
    pet_id: selectedPetId || '',
    type: '',
    date: new Date().toISOString().split('T')[0],
    vet_name: '',
    vet_clinic: '',
    diagnosis: '',
    treatment: '',
    medications: [] as string[],
    cost: undefined as number | undefined,
    next_due_date: '',
    notes: '',
    attachments: [] as string[]
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.pet_id || !formData.type || !formData.date) {
      setErrors({
        pet_id: !formData.pet_id ? 'Please select a pet' : '',
        type: !formData.type ? 'Please select a health type' : '',
        date: !formData.date ? 'Date is required' : ''
      });
      return;
    }

    setLoading(true);
    try {
      await petsService.createPetHealthRecord({
        ...formData,
        date: formData.date || null,
        next_due_date: formData.next_due_date || null
      });
      onSuccess();
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Failed to add health record' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errors.general}</p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="pet_id">Pet *</Label>
        <Select
          value={formData.pet_id}
          onValueChange={(value) => setFormData(prev => ({ ...prev, pet_id: value }))}
        >
          <SelectTrigger className={errors.pet_id ? 'border-red-500' : ''}>
            <SelectValue placeholder="Select a pet" />
          </SelectTrigger>
          <SelectContent>
            {pets.map(pet => (
              <SelectItem key={pet.id} value={pet.id}>
                {pet.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.pet_id && <p className="text-sm text-red-600">{errors.pet_id}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="type">Health Type *</Label>
          <Select
            value={formData.type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
          >
            <SelectTrigger className={errors.type ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select health type" />
            </SelectTrigger>
            <SelectContent>
              {healthTypeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.type && <p className="text-sm text-red-600">{errors.type}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="date">Date *</Label>
          <Input
            id="date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            className={errors.date ? 'border-red-500' : ''}
          />
          {errors.date && <p className="text-sm text-red-600">{errors.date}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="vet_name">Veterinarian Name</Label>
          <Input
            id="vet_name"
            value={formData.vet_name}
            onChange={(e) => setFormData(prev => ({ ...prev, vet_name: e.target.value }))}
            placeholder="Enter veterinarian name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="vet_clinic">Veterinary Clinic</Label>
          <Input
            id="vet_clinic"
            value={formData.vet_clinic}
            onChange={(e) => setFormData(prev => ({ ...prev, vet_clinic: e.target.value }))}
            placeholder="Enter clinic name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="diagnosis">Diagnosis</Label>
        <Textarea
          id="diagnosis"
          value={formData.diagnosis}
          onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
          placeholder="Enter diagnosis"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="treatment">Treatment</Label>
        <Textarea
          id="treatment"
          value={formData.treatment}
          onChange={(e) => setFormData(prev => ({ ...prev, treatment: e.target.value }))}
          placeholder="Enter treatment details"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="cost">Cost ($)</Label>
          <Input
            id="cost"
            type="number"
            step="0.01"
            min="0"
            value={formData.cost || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value ? parseFloat(e.target.value) : undefined }))}
            placeholder="Enter cost"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="next_due_date">Next Due Date</Label>
          <Input
            id="next_due_date"
            type="date"
            value={formData.next_due_date}
            onChange={(e) => setFormData(prev => ({ ...prev, next_due_date: e.target.value }))}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Enter any additional notes"
          rows={3}
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">
          {loading ? 'Adding...' : 'Add Health Record'}
        </Button>
      </div>
    </form>
  );
}

// Edit Health Record Form Component
interface EditHealthRecordFormProps {
  record: PetHealthRecord;
  pets: Pet[];
  onSuccess: () => void;
  onCancel: () => void;
}

function EditHealthRecordForm({ record, pets, onSuccess, onCancel }: EditHealthRecordFormProps) {
  const [formData, setFormData] = useState({
    pet_id: record.pet_id,
    type: record.type,
    date: record.date,
    vet_name: record.vet_name || '',
    vet_clinic: record.vet_clinic || '',
    diagnosis: record.diagnosis || '',
    treatment: record.treatment || '',
    medications: record.medications || [],
    cost: record.cost,
    next_due_date: record.next_due_date || '',
    notes: record.notes || '',
    attachments: record.attachments || []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.type || !formData.date) {
      setErrors({
        type: !formData.type ? 'Please select a health type' : '',
        date: !formData.date ? 'Date is required' : ''
      });
      return;
    }

    setLoading(true);
    try {
      await petsService.updatePetHealthRecord(record.id, formData);
      onSuccess();
    } catch (error) {
      setErrors({ general: error instanceof Error ? error.message : 'Failed to update health record' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errors.general}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="type">Health Type *</Label>
          <Select
            value={formData.type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
          >
            <SelectTrigger className={errors.type ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select health type" />
            </SelectTrigger>
            <SelectContent>
              {healthTypeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.type && <p className="text-sm text-red-600">{errors.type}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="date">Date *</Label>
          <Input
            id="date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            className={errors.date ? 'border-red-500' : ''}
          />
          {errors.date && <p className="text-sm text-red-600">{errors.date}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="vet_name">Veterinarian Name</Label>
          <Input
            id="vet_name"
            value={formData.vet_name}
            onChange={(e) => setFormData(prev => ({ ...prev, vet_name: e.target.value }))}
            placeholder="Enter veterinarian name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="vet_clinic">Veterinary Clinic</Label>
          <Input
            id="vet_clinic"
            value={formData.vet_clinic}
            onChange={(e) => setFormData(prev => ({ ...prev, vet_clinic: e.target.value }))}
            placeholder="Enter clinic name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="diagnosis">Diagnosis</Label>
        <Textarea
          id="diagnosis"
          value={formData.diagnosis}
          onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
          placeholder="Enter diagnosis"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="treatment">Treatment</Label>
        <Textarea
          id="treatment"
          value={formData.treatment}
          onChange={(e) => setFormData(prev => ({ ...prev, treatment: e.target.value }))}
          placeholder="Enter treatment details"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="cost">Cost ($)</Label>
          <Input
            id="cost"
            type="number"
            step="0.01"
            min="0"
            value={formData.cost || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value ? parseFloat(e.target.value) : undefined }))}
            placeholder="Enter cost"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="next_due_date">Next Due Date</Label>
          <Input
            id="next_due_date"
            type="date"
            value={formData.next_due_date}
            onChange={(e) => setFormData(prev => ({ ...prev, next_due_date: e.target.value }))}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Enter any additional notes"
          rows={3}
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">
          {loading ? 'Updating...' : 'Update Health Record'}
        </Button>
      </div>
    </form>
  );
}